'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button, Listbox, ListboxItem, Selection } from '@heroui/react';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaCircleCheck,
  FaScrewdriverWrench,
} from 'react-icons/fa6';
import { FaBoxOpen } from 'react-icons/fa6';
import { Bundle, Service } from '@/util/types/serverTypes';
import { useScheduler } from '../SchedulerContext';
import ServiceListbox from './ServiceListbox';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';
import { useTrackSchedulerScreen } from '../hooks/useTrackSchedulerScreen';

type ServiceConfirmationProps = {
  service: Service;
  onChangeSelection: () => void;
  onNext: () => void;
  onBack?: () => void;
  bundles: Bundle[];
  allServices: Service[];
  cameFromFilterForm?: boolean; // ATT-182: Track if user came from filter form
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function ServiceConfirmation({
  service,
  onChangeSelection,
  onNext,
  onBack,
  bundles,
  allServices,
  cameFromFilterForm = false,
}: ServiceConfirmationProps) {
  const {
    companyStyles,
    scheduleSettings,
    selectedBundle,
    setSelectedBundle,
    selectedAddons,
    setSelectedAddons,
    selectedPrimaryService,
  } = useScheduler();

  useTrackSchedulerScreen({ step: 'services', screen: 'service_confirmation' });

  // Filter bundles relevant to the primary service and active
  const relevantBundles = useMemo(
    () =>
      bundles.filter(
        (bundle) =>
          bundle.active !== false &&
          bundle._primaryServiceId?.some((id) => id === selectedPrimaryService)
      ),
    [bundles, selectedPrimaryService]
  );

  // Get recommended add-ons based on the primary service
  const recommendedAddons = useMemo(() => {
    if (!service?._addonRecommendationIds?.length) return [];
    return allServices
      .filter((s) => service._addonRecommendationIds?.includes(s._id))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [service, allServices]);

  // Get all service IDs that are in selected bundle
  const servicesInSelectedBundle = useMemo(() => {
    const set = new Set<string>();
    if (selectedBundle) {
      const bundle = bundles.find((b) => b._id === selectedBundle);
      bundle?.items.forEach((item) => set.add(item._serviceId));
    }
    return set;
  }, [selectedBundle, bundles]);

  // Create badge map for services in bundles
  const bundleBadgeMap = useMemo(() => {
    const map: Record<string, string> = {};

    if (selectedBundle) {
      servicesInSelectedBundle.forEach((serviceId) => {
        map[serviceId] = 'In Bundle';
      });
    } else {
      relevantBundles.forEach((bundle) => {
        bundle.items.forEach((item) => {
          if (
            item._serviceId !== selectedPrimaryService &&
            !map[item._serviceId]
          ) {
            map[item._serviceId] = `In ${bundle.name}`;
          }
        });
      });
    }

    return map;
  }, [
    selectedBundle,
    servicesInSelectedBundle,
    relevantBundles,
    selectedPrimaryService,
  ]);

  // Auto-deselect add-ons that are in selected bundle
  useEffect(() => {
    if (servicesInSelectedBundle.size > 0) {
      setSelectedAddons((prev: string[]) =>
        prev.filter((addonId: string) => !servicesInSelectedBundle.has(addonId))
      );
    }
  }, [selectedBundle, servicesInSelectedBundle, setSelectedAddons]);

  const handleAddonSelection = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedAddons([]);
      return;
    }
    setSelectedAddons(Array.from(keys as Set<string>));
  };

  const getBundleDetails = (bundle: Bundle) => {
    const usePrimaryFee = scheduleSettings?.bundleDiscountDisplayPrimaryFee ?? true;

    const bundleServices = bundle.items
      .map((item) => allServices.find((s) => s._id === item._serviceId))
      .filter((s): s is Service => s !== undefined);

    // When bundleDiscountDisplayPrimaryFee is on, non-primary services use isPrimaryFee
    // as the "original" baseline so savings reflect the primary-fee comparison, not base fee.
    const originalTotal = bundleServices.reduce((sum, svc) => {
      const isPrimaryService = svc._id === selectedPrimaryService;
      const originalFee =
        usePrimaryFee && !isPrimaryService && svc.isPrimaryFee != null
          ? svc.isPrimaryFee
          : svc.fee;
      return sum + originalFee;
    }, 0);

    // Discounted total is always derived from service.fee (the actual charged amount)
    const discountedTotal = bundle.items.reduce((sum, item) => {
      const svc = allServices.find((s) => s._id === item._serviceId);
      if (!svc) return sum;
      const discount =
        item.discountType === 'percent'
          ? svc.fee * (item.discountValue / 100)
          : item.discountValue;
      return sum + (svc.fee - discount);
    }, 0);

    return {
      bundleServices,
      discountedTotal,
      savings: originalTotal - discountedTotal,
    };
  };

  const hasAdditionalOptions =
    relevantBundles.length > 0 || recommendedAddons.length > 0;

  // ATT-182: Hide addon recommendations if came from filter form and service has AI addons enabled
  const shouldHideAddons =
    cameFromFilterForm && service.aiUpsellAddons === true;
  const [showAddons, setShowAddons] = useState(!shouldHideAddons);

  // Calculate delay offsets based on what sections exist
  const bundlesDelay = 0.3;
  const addonsDelay = relevantBundles.length > 0 ? 0.5 : 0.3;
  const buttonsDelay = hasAdditionalOptions ? 0.7 : 0.3;
  /** Stagger after primary actions when the collapsed add-on shortcut is shown */
  const collapsedAddonShortcutDelay = buttonsDelay + 0.15;

  return (
    <div className='space-y-6'>
      {/* Great Choice Confirmation Card */}
      <motion.div
        className='mx-auto max-w-2xl text-center'
        variants={fadeInUp}
        initial='initial'
        animate='animate'
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <motion.div
          className='mb-4 flex justify-center'
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3, ease: 'backOut' }}
        >
          <div className='flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
            <FaCircleCheck className='text-3xl text-green-600' />
          </div>
        </motion.div>
        <h3 className='mb-2 text-2xl font-bold text-gray-900'>Great choice!</h3>
        {!shouldHideAddons && (
          <p className='mb-4 text-gray-600'>
            {hasAdditionalOptions
              ? 'Would you like to add any additional services?'
              : 'You can proceed to the next step.'}
          </p>
        )}
        <div className='mb-6 rounded-xl border-2 border-green-200 bg-green-50 p-4'>
          <div className='flex items-start gap-3'>
            <div className='shrink-0'>
              <div
                className='flex h-10 w-10 items-center justify-center rounded-lg'
                style={{
                  backgroundColor: companyStyles.companyColor
                    ? `${companyStyles.companyColor}66`
                    : 'rgba(0, 111, 238, 0.4)',
                  color: companyStyles.companyColor || '#006FEE',
                }}
              >
                <FaScrewdriverWrench className='text-lg' />
              </div>
            </div>
            <div className='flex-1 text-left'>
              <p className='font-semibold text-gray-900'>{service.name}</p>
              <p className='text-sm text-gray-600'>{service.description}</p>
              {scheduleSettings.showPricingInOnlineScheduler && (
                <p className='mt-2 text-sm text-gray-700'>
                  Starting at ${service.fee}
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bundles Section */}
      {relevantBundles.length > 0 && (!shouldHideAddons || showAddons) && (
        <motion.div
          className='space-y-4'
          variants={fadeInUp}
          initial='initial'
          animate='animate'
          transition={{ duration: 0.4, delay: bundlesDelay, ease: 'easeOut' }}
        >
          <div className='space-y-2 text-center'>
            <h4 className='text-lg font-bold'>Service Bundles</h4>
            <p className='text-sm text-gray-600'>
              Save money by bundling services together
            </p>
          </div>
          <div className='flex justify-center'>
            <Listbox
              aria-label='Service Bundles Selection'
              variant='flat'
              selectionMode='single'
              selectedKeys={selectedBundle ? [selectedBundle] : []}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys) as string[];
                setSelectedBundle(selected[0] || null);
              }}
              className='max-w-2xl rounded-xl border-2 border-warning bg-warning-50 p-4 shadow-xl shadow-warning-100'
            >
              {relevantBundles.map((bundle) => {
                const { bundleServices, discountedTotal, savings } =
                  getBundleDetails(bundle);
                return (
                  <ListboxItem
                    key={bundle._id}
                    className='py-1'
                    classNames={{ description: 'whitespace-normal' }}
                    textValue={bundle.name}
                    startContent={
                      <div className='min-h-8 flex min-w-8 items-center justify-center rounded-lg bg-purple-100'>
                        <FaBoxOpen className='text-lg text-purple-600' />
                      </div>
                    }
                    endContent={
                      <div className='text-right'>
                        {scheduleSettings?.showPricingInOnlineScheduler && (
                          <div className='font-semibold text-purple-600'>
                            ${discountedTotal.toFixed(2)}
                          </div>
                        )}
                        {savings > 0 && (
                          <div className='text-xs text-green-600'>
                            Save ${savings.toFixed(2)}
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div className='space-y-1'>
                      <div className='font-semibold'>{bundle.name}</div>
                      {bundle.description && (
                        <div className='text-xs text-gray-600'>
                          {bundle.description}
                        </div>
                      )}
                      <div className='text-xs text-gray-500'>
                        Includes:{' '}
                        {bundleServices.map((s, idx) => (
                          <span key={s._id}>
                            {s.name}
                            {idx < bundleServices.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </ListboxItem>
                );
              })}
            </Listbox>
          </div>
        </motion.div>
      )}

      {/* Recommended Add-ons Section (expanded only; collapsed shortcut sits below primary actions) */}
      {recommendedAddons.length > 0 && (!shouldHideAddons || showAddons) && (
        <motion.div
          className='space-y-4'
          variants={fadeInUp}
          initial='initial'
          animate='animate'
          transition={{ duration: 0.4, delay: addonsDelay, ease: 'easeOut' }}
        >
          <div className='space-y-2 text-center'>
            <h4 className='text-lg font-bold'>
              Additional Recommended Services
            </h4>
            <p className='text-sm text-gray-600'>
              Enhance your {service.name} with these additional services
            </p>
          </div>
          <div className='flex justify-center'>
            <ServiceListbox
              services={recommendedAddons}
              selectedKeys={new Set(selectedAddons)}
              onSelectionChange={handleAddonSelection}
              selectionMode='multiple'
              disabledKeys={
                servicesInSelectedBundle.size > 0
                  ? new Set(Array.from(servicesInSelectedBundle))
                  : undefined
              }
              ariaLabel='Recommended additional services'
              className='max-w-2xl rounded-xl border bg-white p-4'
              iconBackgroundColor={
                companyStyles.companyColor
                  ? `${companyStyles.companyColor}66`
                  : undefined
              }
              iconTextColor={companyStyles.companyColor || undefined}
              priceFormat='addon'
              showPrice={scheduleSettings?.showPricingInOnlineScheduler}
              badgeMap={bundleBadgeMap}
            />
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <motion.div
        className='flex justify-center gap-3'
        variants={fadeInUp}
        initial='initial'
        animate='animate'
        transition={{ duration: 0.4, delay: buttonsDelay, ease: 'easeOut' }}
      >
        {onBack && (
          <Button variant='light' onPress={onBack} className='text-gray-600'>
            Back
          </Button>
        )}
        <Button variant='flat' color='default' onPress={onChangeSelection}>
          Change Selection
        </Button>
        <Button
          endContent={<FaArrowRight />}
          onPress={() => {
            posthog.capture(EVENTS.schedulerServiceSelected, {
              service_name: service.name,
              service_id: service._id,
              addon_count: selectedAddons.length,
              bundle_selected: selectedBundle ?? null,
            });
            onNext();
          }}
          className={companyStyles.secondaryButtonClassName}
          style={companyStyles.secondaryButtonStyles}
        >
          Next
        </Button>
      </motion.div>

      {recommendedAddons.length > 0 && shouldHideAddons && !showAddons && (
        <motion.div
          className='flex justify-center pt-4'
          variants={fadeInUp}
          initial='initial'
          animate='animate'
          transition={{
            duration: 0.4,
            delay: collapsedAddonShortcutDelay,
            ease: 'easeOut',
          }}
        >
          <Button
            variant='bordered'
            onPress={() => setShowAddons(true)}
            className='max-w-2xl'
          >
            Already know what additional services you want? Select here
          </Button>
        </motion.div>
      )}
    </div>
  );
}
